import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const RESOLUTION_LEVELS = [80, 90, 100, 110, 120];
const DEFAULT_RESOLUTION = 100;

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'light';
  });

  const [resolution, setResolution] = useState(() => {
    // Check localStorage for saved resolution preference
    const savedResolution = localStorage.getItem('resolution');
    if (savedResolution) {
      const parsed = parseInt(savedResolution, 10);
      if (RESOLUTION_LEVELS.includes(parsed)) {
        return parsed;
      }
    }
    return DEFAULT_RESOLUTION;
  });

  useEffect(() => {
    // Update document class and save to localStorage
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    // Apply resolution zoom to document and save to localStorage
    const root = document.documentElement;
    root.style.fontSize = `${resolution}%`;
    localStorage.setItem('resolution', resolution.toString());
  }, [resolution]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const increaseResolution = () => {
    setResolution(prev => {
      const currentIndex = RESOLUTION_LEVELS.indexOf(prev);
      if (currentIndex < RESOLUTION_LEVELS.length - 1) {
        return RESOLUTION_LEVELS[currentIndex + 1];
      }
      return prev;
    });
  };

  const decreaseResolution = () => {
    setResolution(prev => {
      const currentIndex = RESOLUTION_LEVELS.indexOf(prev);
      if (currentIndex > 0) {
        return RESOLUTION_LEVELS[currentIndex - 1];
      }
      return prev;
    });
  };

  const canIncreaseResolution = resolution < RESOLUTION_LEVELS[RESOLUTION_LEVELS.length - 1];
  const canDecreaseResolution = resolution > RESOLUTION_LEVELS[0];

  return (
    <ThemeContext.Provider value={{
      theme,
      toggleTheme,
      resolution,
      increaseResolution,
      decreaseResolution,
      canIncreaseResolution,
      canDecreaseResolution
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
